// Console Add/Edit-Agent form (fn-71 / ADR 0035 / R11).
//
// Admin-only authoring surface for DB-backed (`source='console'`) agents:
// name / model / system prompt / MCP-server + skill references + the
// `<AgentToolConfigPanel>` (`authoring` mode) for per-tool baseline
// permissions. On submit it calls the BFF `agents.create` / `agents.update`
// (R9 — the control plane is the platform-admin authority + validator) and
// navigates to the saved agent. Plain CSS over Smokeball tokens (no
// Tailwind/shadcn — CLAUDE.md).
//
// **Connectors (fn-73)** — the form lists EVERY registered MCP connector from
// `mcpServers.list`: the single `is_gateway && selectable` row drives the fn-72
// WHOLE-GATEWAY per-tool panel below, while every OTHER `selectable` connector
// (e.g. a direct `type:url` server like InfoTrack) is a plain enable/disable
// toggle in form state (`nonGatewaySelected`), NOT routed through the panel. A
// non-gateway connector is a whole-server grant LOCKED to `always_ask` (the
// platform can't enumerate a direct-url server's tools, so it can't tell which
// are denylisted — HITL-on-every-call is denylist-safe). Non-`ok` rows render
// DISABLED with their scan status. `buildAgentTools` merges the gateway Shape-B
// `tools[]` with the non-gateway whole-server grants on submit.
//
// **One unified gateway tool-data path** — when the gateway is enabled BOTH
// create and edit drive the tool panel from `agents.draftConfigurableTools`
// (`{ whole_gateway: true }`) re-projected from the live catalog. EDIT seeds the
// panel via `applyPriorToolConfig`, OVERLAYING the agent's saved per-tool
// baselines so a save PRESERVES them (never silently re-defaults / widens — a
// now-locked tool is never re-widened). When the gateway is OFF the panel runs
// native-builtins-only (`{ mcp_servers: [] }`) — it ALWAYS renders, so
// `authoringConfig` is non-null and submit's `authoringReady` gate holds.
//
// **Degraded catalog blocks save ONLY when the gateway is enabled** (fn-73 R3) —
// when the live gateway catalog can't be fully read
// (`AuthoringToolConfig.catalogAvailable === false`) the emitted gateway baselines
// would be incomplete, so saving is disabled until it loads (Retry is in the
// panel). An agent with only non-gateway connectors (gateway off) emits
// whole-server grants that never read the catalog, so it MUST save even with the
// gateway catalog down (`isCatalogDegradedBlocking`).
//
// **Admin gate:** the page hides itself from non-admins via `useIsPlatformAdmin`
// (the console has no other admin signal — see that hook). The control plane is
// the real gate; this is the UX affordance.
//
// Every secondary list (skills, MCP servers) + the panel branch
// loading/error/empty distinctly (memory `secondary-query-joins-must-branch-on`).

import {
  AgentToolConfigPanel,
  type AuthoringToolConfig,
  type ToolConfigLoadState,
  type ToolConfigPanelState,
} from '@sb-ai-platform/ui-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { trpc } from '../../trpc-client.js';
import {
  type ConfigurableToolsResponse,
  type PriorToolConfig,
  applyPriorToolConfig,
  buildAgentTools,
  buildAgentToolsForEdit,
  isValidAgentName,
  readAgentMcpServers,
  readAgentSkills,
  readModelString,
  readNetworkProfileName,
  readPriorToolConfig,
  readSavedMcpToolsets,
} from './authoring.js';
import { useIsPlatformAdmin } from './use-is-platform-admin.js';

const LIST_LIMIT = 200;

// ── Connector list (fn-73 — gateway + every other registered MCP connector) ───

/**
 * One `mcpServers.list` row, narrowed to the fields the connector UI needs
 * (fn-73). `is_gateway` + `selectable` are the .1 backend signal; `name` IS the
 * registry `mcp_server_name` the converter persists for a non-gateway connector.
 * Structural subset of the BFF `McpServerRow` — kept local so the console
 * doesn't import across the BFF boundary.
 */
export interface McpConnectorRow {
  readonly name: string;
  readonly scan_status: string;
  readonly is_gateway: boolean;
  readonly selectable: boolean;
  /**
   * fn-74 (R3) — CP-derived: `true` only for the InfoTrack-backed connector whose
   * credential-kind is InfoTrack AND whose resolved URL origin is allowlisted. A
   * SELECTED direct connector is sent in `direct_mcp_servers` so the CP
   * introspects its OWN `tools/list` per-tool; a selected non-direct non-gateway
   * connector stays the fn-73 whole-server toggle. OPTIONAL (a `false` default) so
   * a pre-fn-74 row / test fixture without the flag is treated as non-direct.
   */
  readonly supports_direct_introspection?: boolean;
}

/**
 * The connector list split into the single selectable gateway row (drives the
 * fn-72 grouped panel) and the non-gateway rows (plain enable/disable toggles).
 * Non-`ok` / non-selectable rows are kept so the UI can render them DISABLED with
 * their scan status (R-minor) — `selectable` says whether they can be toggled.
 */
export interface ConnectorList {
  /**
   * The single `is_gateway && selectable` row, or `null` (0 matches, a non-`ok`
   * gateway row, or a >1-match conflict where every matching row failed closed
   * to `selectable:false`). When `null`, the panel runs native-builtins-only.
   */
  readonly gateway: McpConnectorRow | null;
  /** A gateway-matched row that is NOT selectable (non-`ok` scan or conflict). */
  readonly gatewayDisabled: McpConnectorRow | null;
  /** Every non-gateway connector row (selectable AND non-selectable), in order. */
  readonly nonGateway: readonly McpConnectorRow[];
}

/**
 * Split `mcpServers.list` rows into the gateway connector + the non-gateway
 * connectors (fn-73 R1/R2). The gateway is the single `is_gateway && selectable`
 * row; a gateway-matched but non-selectable row (non-`ok` scan, or one arm of a
 * >1-match conflict — already `selectable:false` from the backend) is surfaced
 * separately as `gatewayDisabled` so the UI shows it disabled with status. Every
 * row that is NOT a gateway match is a non-gateway connector (selectable or not).
 * Pure so the split is unit-testable without mounting.
 */
export function splitConnectors(rows: readonly McpConnectorRow[]): ConnectorList {
  let gateway: McpConnectorRow | null = null;
  let gatewayDisabled: McpConnectorRow | null = null;
  const nonGateway: McpConnectorRow[] = [];
  for (const row of rows) {
    if (row.is_gateway) {
      if (row.selectable) {
        // Exactly-one selectable gateway (the backend guarantees at most one).
        if (gateway === null) gateway = row;
      } else if (gatewayDisabled === null) {
        gatewayDisabled = row;
      }
      continue;
    }
    nonGateway.push(row);
  }
  return { gateway, gatewayDisabled, nonGateway };
}

/**
 * The non-gateway connectors initially ENABLED (fn-73 R5). Create: none. Edit:
 * a connector is on only when its registry `name` is in the agent's saved
 * `mcp_server_name` set — a newly-registered connector stays OFF (no silent
 * widening). Only `selectable` rows can be seeded on (a non-`ok` row is never
 * toggled). Returns the ENABLED names, in list order.
 */
export function initialNonGatewaySelection(
  nonGateway: readonly McpConnectorRow[],
  savedServers: readonly string[] | undefined,
): string[] {
  if (savedServers === undefined) return [];
  const saved = new Set(savedServers);
  return nonGateway.filter((row) => row.selectable && saved.has(row.name)).map((row) => row.name);
}

/**
 * Whether the gateway connector is initially ENABLED (fn-73 R5 — no silent
 * widening, set AFTER the list resolves). Create (`savedServers === undefined`):
 * ON only when exactly one selectable gateway row exists. Edit: ON only when the
 * single selectable gateway row's `name` is in the agent's saved
 * `mcp_server_name` set (a saved-name match) — so an InfoTrack-only agent (no
 * saved gateway membership) does NOT seed the gateway enabled.
 */
export function initialGatewayEnabled(
  gateway: McpConnectorRow | null,
  savedServers: readonly string[] | undefined,
): boolean {
  if (gateway === null) return false;
  if (savedServers === undefined) return true; // create: exactly-one gateway → on
  return savedServers.includes(gateway.name);
}

// ── Direct (InfoTrack) connector routing (fn-74 R3/R6) ────────────────────────

/**
 * The set of non-gateway connector `name`s that are DIRECT-introspectable AND
 * selectable (fn-74 R3) — the rows whose tools the CP can read per-tool via
 * `direct_mcp_servers`. A non-`ok`/non-selectable direct row is excluded (it can't
 * be introspected anyway). Used to (a) route a SELECTED such connector into the
 * draft `direct_mcp_servers` (→ the per-tool panel) instead of the fn-73
 * whole-server toggle, and (b) tell the converter which `mcp_server_name`s are
 * direct so a degraded one's fallback is the `always_ask` grant. Pure.
 */
export function directServerNameSet(nonGateway: readonly McpConnectorRow[]): Set<string> {
  const out = new Set<string>();
  for (const row of nonGateway) {
    if (row.selectable && row.supports_direct_introspection === true) out.add(row.name);
  }
  return out;
}

/**
 * Partition the SELECTED non-gateway connector names into the DIRECT ones (→
 * `direct_mcp_servers`, projected per-tool through the panel) and the PLAIN ones
 * (→ the fn-73 whole-server `always_ask` toggle). A selected name that is not a
 * known direct server is PLAIN. Order preserved within each bucket. Pure.
 */
export function partitionSelectedNonGateway(
  selected: readonly string[],
  directNames: ReadonlySet<string>,
): { readonly direct: string[]; readonly plain: string[] } {
  const direct: string[] = [];
  const plain: string[] = [];
  for (const name of selected) {
    if (directNames.has(name)) direct.push(name);
    else plain.push(name);
  }
  return { direct, plain };
}

/** The draft-config preview query body (the input to `agents.draftConfigurableTools`). */
export interface DraftConfigurableToolsQuery {
  readonly mcp_servers?: string[];
  readonly whole_gateway?: boolean;
  readonly direct_mcp_servers?: string[];
}

/**
 * Build the draft `agents.draftConfigurableTools` query body from the connector
 * selection (fn-74 R3). The gateway (when enabled) requests the WHOLE-GATEWAY
 * projection; the selected DIRECT connectors are requested per-tool via
 * `direct_mcp_servers` (the CP introspects each one's own `tools/list`). The
 * acceptance shapes:
 *   - gateway + direct → `{ whole_gateway:true, direct_mcp_servers:[...] }` (BOTH).
 *   - direct only      → `{ mcp_servers:[], direct_mcp_servers:[...] }` (just direct).
 *   - gateway only     → `{ whole_gateway:true }`.
 *   - neither          → `{ mcp_servers:[] }` (native-builtins-only — a valid
 *                        no-MCP agent; never a stale connector).
 * `mcp_servers: []` is included ONLY when the gateway is OFF (the whole-gateway
 * flag supersedes the explicit-server list). Pure.
 */
export function buildDraftConfigurableToolsQuery(input: {
  readonly gatewayEnabled: boolean;
  readonly selectedDirectServers: readonly string[];
}): DraftConfigurableToolsQuery {
  const directServers =
    input.selectedDirectServers.length > 0
      ? { direct_mcp_servers: [...input.selectedDirectServers] }
      : {};
  if (input.gatewayEnabled) return { whole_gateway: true, ...directServers };
  return { mcp_servers: [], ...directServers };
}

/**
 * The enabled native tool names of a SAVED direct connector's `mcp_toolset`
 * entry (fn-74) — the `configs[].name` with `enabled !== false`. A whole-server
 * grant (`default_config.enabled === true`, no allow-list) yields the empty set
 * (no per-tool names to preserve). Used to decide whether the introspected
 * catalog can faithfully represent the saved per-tool config. Pure.
 */
export function savedDirectToolNames(entry: Record<string, unknown> | undefined): Set<string> {
  const out = new Set<string>();
  if (entry === undefined) return out;
  const configs = entry.configs;
  if (!Array.isArray(configs)) return out;
  for (const cfg of configs) {
    if (cfg === null || typeof cfg !== 'object') continue;
    const c = cfg as Record<string, unknown>;
    if (typeof c.name !== 'string') continue;
    const name = c.name.trim();
    if (name.length === 0 || c.enabled === false) continue;
    out.add(name);
  }
  return out;
}

/**
 * Whether a SELECTED, introspected DIRECT connector can be faithfully
 * REPRESENTED from the panel (fn-74 — preserve-don't-strip). Representable only
 * when the panel currently has its INTROSPECTED catalog (`catalog_unavailable`
 * false — NOT login-required / degraded) AND every saved enabled native tool name
 * is present in that catalog. When the firm's catalog LACKS a saved tool (a
 * globally-saved per-tool config from another firm), rebuilding from form state
 * would silently STRIP it — so it is NOT representable and its saved grant is
 * preserved verbatim. A connector with NO saved per-tool names (new selection, or
 * a saved whole-server grant) is representable once introspected. Pure.
 */
export function directConnectorRepresentable(input: {
  readonly serverId: string;
  readonly data: ConfigurableToolsResponse | undefined;
  readonly savedToolNames: ReadonlySet<string>;
}): boolean {
  if (input.data === undefined) return false;
  const connector = input.data.connectors.find((c) => c.server_id === input.serverId);
  // Not in the panel data yet, or login-required / degraded → can't represent.
  if (connector === undefined || connector.catalog_unavailable) return false;
  if (input.savedToolNames.size === 0) return true;
  const introspected = new Set(connector.tools.map((t) => t.tool_id));
  for (const saved of input.savedToolNames) {
    if (!introspected.has(saved)) return false;
  }
  return true;
}

/**
 * The set of saved `mcp_server_name`s the EDIT form can currently REPRESENT from
 * its live state (fn-73 — CodeRabbit data-integrity fix). A saved connector NOT
 * in this set is preserved verbatim by {@link buildAgentToolsForEdit} rather than
 * silently stripped. The set is:
 *   - the present+selectable gateway connector's own name, PLUS every federated
 *     `target_id` it folds in (fn-72 whole-gateway — a legacy per-target
 *     `mcp_server_name` like `archie`/`forms` now surfaces under the ONE gateway
 *     connector, so the panel re-emits its tools and the saved entry must NOT also
 *     be preserved); and
 *   - every present+selectable PLAIN (non-direct) non-gateway connector's name; and
 *   - every present+selectable DIRECT connector that is INTROSPECTED and whose
 *     introspected catalog contains all its saved tools (fn-74 — a direct
 *     connector that is login-required/degraded, or whose saved per-tool config
 *     the firm's catalog lacks, is EXCLUDED so its saved grant is preserved
 *     verbatim rather than stripped/widened).
 * A connector that is absent / `selectable:false` (incl. one that flipped
 * non-selectable after a refetch) is EXCLUDED — so stale form state can never
 * leak it into the submitted set; it falls into the preserved-original bucket.
 *
 * `gatewayTargetIds` is the gateway connector's folded `target_id` set, derived
 * from the live tool catalog; pass an empty set when the catalog isn't loaded.
 * `panelData` + `savedToolsets` drive the fn-74 direct-connector representability
 * check; pass them undefined (e.g. on CREATE) to skip the direct refinement.
 */
export function representableServerNames(input: {
  readonly connectors: ConnectorList;
  readonly gatewayTargetIds: ReadonlySet<string>;
  readonly panelData?: ConfigurableToolsResponse | undefined;
  readonly savedToolsets?: ReadonlyMap<string, Record<string, unknown>> | undefined;
}): Set<string> {
  const names = new Set<string>();
  if (input.connectors.gateway !== null) {
    names.add(input.connectors.gateway.name);
    for (const targetId of input.gatewayTargetIds) {
      if (targetId.length > 0) names.add(targetId);
    }
  }
  for (const row of input.connectors.nonGateway) {
    if (!row.selectable) continue;
    const savedEntry = input.savedToolsets?.get(row.name);
    if (row.supports_direct_introspection === true) {
      // A DIRECT connector is representable only when its introspected catalog
      // can faithfully carry every saved per-tool name (fn-74 preserve-don't-strip).
      const representable = directConnectorRepresentable({
        serverId: row.name,
        data: input.panelData,
        savedToolNames: savedDirectToolNames(savedEntry),
      });
      if (representable) names.add(row.name);
      continue;
    }
    // A connector that USED to support direct introspection (so it has saved
    // per-tool `configs`) but no longer does must NOT be silently widened to a
    // whole-server grant — exclude it so its saved allow-list is preserved
    // verbatim by the edit-seeding preserve path (CodeRabbit #216).
    if (savedEntry !== undefined && Array.isArray(savedEntry.configs)) continue;
    names.add(row.name);
  }
  return names;
}

/**
 * The gateway connector's folded federated `target_id`s, derived from the live
 * tool catalog (fn-72 whole-gateway). One gateway connector surfaces tools across
 * many targets; each tool's `target_id` is a `mcp_server_name` a LEGACY agent may
 * have saved as its own connector before the gateway was one connector. Returns
 * `target_id` ∪ `server_id` so the gateway's own name is covered too. Empty when
 * the catalog hasn't loaded.
 */
export function gatewayFoldedTargetIds(
  data: ConfigurableToolsResponse | undefined,
  gatewayName: string | null,
): Set<string> {
  const ids = new Set<string>();
  if (data === undefined || gatewayName === null) return ids;
  for (const connector of data.connectors) {
    if (connector.server_id !== gatewayName) continue;
    ids.add(connector.server_id);
    for (const tool of connector.tools) {
      if (typeof tool.target_id === 'string' && tool.target_id.length > 0) {
        ids.add(tool.target_id);
      }
    }
  }
  return ids;
}

/**
 * The saved connector names whose persisted `mcp_toolset` will be PRESERVED
 * VERBATIM on save because the form can't currently represent them (fn-73 — for a
 * read-only "saved, not editable right now" note). A saved name is preserved when
 * it is NOT in the representable set AND is not a federated target already folded
 * into the gateway view. Returns the names in saved order. Pure.
 */
export function preservedSavedConnectorNames(input: {
  readonly savedToolsets: ReadonlyMap<string, Record<string, unknown>> | undefined;
  readonly representable: ReadonlySet<string>;
}): string[] {
  if (input.savedToolsets === undefined) return [];
  const out: string[] = [];
  for (const name of input.savedToolsets.keys()) {
    if (!input.representable.has(name)) out.push(name);
  }
  return out;
}

/**
 * Reconcile the retained non-gateway selection against the current connector list
 * (fn-73 finding 3). After a `mcpServers.list` refetch a previously-selected
 * connector may have disappeared or flipped `selectable:false`; the retained
 * selection state would otherwise still submit it. Drop every name that is no
 * longer a selectable non-gateway row. Returns the SAME array reference when
 * nothing changed (so a setState no-op doesn't churn).
 */
export function reconcileNonGatewaySelection(
  selected: readonly string[],
  nonGateway: readonly McpConnectorRow[],
): readonly string[] {
  const selectable = new Set(nonGateway.filter((r) => r.selectable).map((r) => r.name));
  const next = selected.filter((name) => selectable.has(name));
  return next.length === selected.length ? selected : next;
}

/**
 * Friendly status text for a non-selectable connector row (R-minor) — explains
 * WHY it can't be toggled. Maps the registry `scan_status` to a short reason; an
 * unknown / future status falls back to the raw value so nothing is swallowed.
 */
export function connectorStatusText(scanStatus: string): string {
  switch (scanStatus) {
    case 'no_endpoint':
      return 'Unavailable — no endpoint configured.';
    case 'invalid_config':
      return 'Unavailable — invalid configuration.';
    case 'scan_failed':
      return 'Unavailable — last scan failed.';
    case 'ok':
      // `ok` but non-selectable → a gateway-match conflict (>1 descriptor matched
      // the gateway URL → all matching rows fail closed).
      return 'Unavailable — ambiguous gateway match (config conflict).';
    default:
      return `Unavailable — ${scanStatus}.`;
  }
}

/**
 * The submit-gate inputs (R-quickfix-1). Pure-function shape so the
 * first-unmet-condition hint is unit-testable without mounting the form.
 */
export interface SubmitGateState {
  readonly nameValid: boolean;
  readonly model: string;
  readonly system: string;
  /** The connector (`mcpServers.list`) query has resolved successfully. */
  readonly connectorsReady: boolean;
  /** The connector (`mcpServers.list`) query errored. */
  readonly connectorsErrored: boolean;
  readonly catalogDegraded: boolean;
  readonly panelReady: boolean;
  readonly authoringReady: boolean;
}

/**
 * The FIRST unmet submit-gate condition, as a friendly hint — or `null` when
 * every condition is met (the button is enabled). Mirrors the `canSubmit` checks
 * IN ORDER so the hint always names the next thing to fix (R-quickfix-1).
 */
export function computeSubmitHint(state: SubmitGateState): string | null {
  if (!state.nameValid) return 'Enter a valid agent name';
  if (state.model.trim().length === 0) return 'Choose a model';
  if (state.system.trim().length === 0) return 'Enter a system prompt';
  // The connector selection feeds the whole-gateway grant; never save before it
  // resolves (the connector checkbox is unconfirmed while pending/errored, so a
  // hidden connector state must not leak into the saved payload — fn-72 R3 fix).
  if (state.connectorsErrored)
    return 'The connector list is unavailable right now — try again shortly.';
  if (!state.connectorsReady) return 'Loading connectors…';
  if (state.catalogDegraded)
    return 'The tool catalog is unavailable right now — try again shortly.';
  if (!state.panelReady || !state.authoringReady) return 'Loading tools…';
  return null;
}

/**
 * Whether the catalog-unavailable save-block applies (fn-73 R3). It applies ONLY
 * when the gateway connector is ENABLED — the gateway panel needs the live
 * gateway catalog to emit complete per-tool baselines, so an incomplete read
 * must block save (memory `agent-edit-ui-must-not-re-defaultwiden`). An agent
 * with only non-gateway connectors (gateway off) emits whole-server grants that
 * never read the catalog, so it MUST save even if the gateway catalog is down.
 */
export function isCatalogDegradedBlocking(input: {
  readonly gatewayEnabled: boolean;
  readonly authoringConfig: { readonly catalogAvailable: boolean } | null;
}): boolean {
  return (
    input.gatewayEnabled &&
    input.authoringConfig !== null &&
    !input.authoringConfig.catalogAvailable
  );
}

/**
 * The authoring fields common to create + update (R-quickfix-2). Pure builder so
 * the `network_profile` include/omit rule is unit-testable without mounting the
 * form. `network_profile` is carried ONLY when a non-blank profile is selected;
 * a blank selection ("(agent default)") omits it so the CP applies the org
 * default.
 */
export function buildAgentSubmitShared(input: {
  readonly model: string;
  readonly system: string;
  readonly description: string;
  readonly skills: readonly string[];
  readonly tools: Record<string, unknown>[];
  readonly networkProfile: string;
}): {
  model: string;
  system: string;
  description?: string;
  skills: string[];
  tools: Record<string, unknown>[];
  network_profile?: string;
} {
  return {
    model: input.model.trim(),
    system: input.system,
    ...(input.description.trim().length > 0 ? { description: input.description.trim() } : {}),
    skills: [...input.skills],
    tools: input.tools,
    ...(input.networkProfile.trim().length > 0
      ? { network_profile: input.networkProfile.trim() }
      : {}),
  };
}

/** Map a tRPC query's discriminated state → the panel's `ToolConfigLoadState`. */
function toLoadState(query: {
  readonly isPending: boolean;
  readonly error: { readonly message: string } | null;
  readonly data: ConfigurableToolsResponse | undefined;
}): ToolConfigLoadState {
  if (query.error !== null) return { kind: 'error', message: query.error.message };
  if (query.isPending || query.data === undefined) return { kind: 'loading' };
  return { kind: 'ready', data: query.data };
}

// ── Model registry dropdown (R2) ──────────────────────────────────────────────

/**
 * One canonical model from the registry endpoint (fn-72 R1). Structural subset
 * of the BFF `ModelItem` — kept local so the console doesn't import across the
 * BFF boundary. `name` IS the model id forwarded verbatim to MA on save.
 */
export interface ModelOption {
  readonly name: string;
  readonly label: string;
  readonly is_default: boolean;
}

/**
 * The Model field's discriminated render state. `ready` shows the dropdown; every
 * other state falls back to the free-text id input (the co-equal manual path,
 * memory `ai-features-offer-manual-path`) so authoring is never blocked. Branches
 * loading/error/empty distinctly (memory `secondary-query-joins-must-branch-on`).
 */
export type ModelFieldState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'empty' }
  | { readonly kind: 'ready'; readonly models: readonly ModelOption[] };

/** Map the `models.list` query's discriminated state → the Model field state. */
export function computeModelFieldState(query: {
  readonly isPending: boolean;
  readonly error: { readonly message: string } | null;
  readonly data: { readonly data: readonly ModelOption[] } | undefined;
}): ModelFieldState {
  if (query.error !== null) return { kind: 'error', message: query.error.message };
  if (query.isPending || query.data === undefined) return { kind: 'loading' };
  if (query.data.data.length === 0) return { kind: 'empty' };
  return { kind: 'ready', models: query.data.data };
}

/** The registry's default-selected model id, or `null` if none is flagged. */
export function pickDefaultModel(models: readonly ModelOption[]): string | null {
  return models.find((m) => m.is_default)?.name ?? null;
}

/**
 * Whether the currently-selected model needs an extra fallback `<option>` to stay
 * selectable — true when a non-blank value isn't in the registry list (e.g. an
 * agent saved on an older/pinned model that the registry no longer surfaces). Keeps
 * an edited agent's saved model selectable without silently dropping it.
 */
export function savedModelNeedsOption(
  models: readonly ModelOption[],
  currentModel: string,
): boolean {
  return currentModel.length > 0 && !models.some((m) => m.name === currentModel);
}

// ── Route entry points ──────────────────────────────────────────────────────

export function AgentNewPage(): React.ReactElement {
  return <AgentFormGate mode="create" />;
}

export function AgentEditPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const agentId = params.id ?? '';
  if (agentId.length === 0) {
    return (
      <section className="page-agent-form" role="alert">
        <h1>Edit agent</h1>
        <p>Missing agent id.</p>
      </section>
    );
  }
  return <AgentFormGate mode="edit" agentId={agentId} />;
}

// ── Admin gate ──────────────────────────────────────────────────────────────

function AgentFormGate(props: {
  readonly mode: 'create' | 'edit';
  readonly agentId?: string;
}): React.ReactElement {
  const admin = useIsPlatformAdmin();
  const title = props.mode === 'create' ? 'Add agent' : 'Edit agent';
  if (!admin.isResolved) {
    return (
      <section className="page-agent-form" aria-busy="true">
        <h1>{title}</h1>
        <p>Checking access…</p>
      </section>
    );
  }
  if (!admin.isAdmin) {
    return (
      <section className="page-agent-form" role="alert">
        <header className="agent-detail-header">
          <Link to="/agents" className="agent-detail-back">
            ← Agents
          </Link>
        </header>
        <h1>{title}</h1>
        <p>You need platform-admin access to author agents.</p>
      </section>
    );
  }
  return props.mode === 'create' ? (
    <AgentForm mode="create" title="Add agent" />
  ) : (
    <AgentEditLoader agentId={props.agentId ?? ''} />
  );
}

// ── Edit — load the agent, then render the form seeded from its snapshot ──────

function AgentEditLoader({ agentId }: { readonly agentId: string }): React.ReactElement {
  const agentQuery = trpc.agents.byId.useQuery({ agentId }, { enabled: agentId.length > 0 });

  if (agentQuery.isPending) {
    return (
      <section className="page-agent-form" aria-busy="true">
        <h1>Edit agent</h1>
        <p>Loading agent…</p>
      </section>
    );
  }
  if (agentQuery.error !== null) {
    return (
      <section className="page-agent-form" role="alert">
        <header className="agent-detail-header">
          <Link to="/agents" className="agent-detail-back">
            ← Agents
          </Link>
        </header>
        <h1>Edit agent</h1>
        <p>Failed to load agent: {agentQuery.error.message}</p>
      </section>
    );
  }

  const agent = agentQuery.data;
  if (agent.source !== 'console') {
    // A config-repo agent is edited via the repo only (the PATCH would 409).
    return (
      <section className="page-agent-form" role="alert">
        <header className="agent-detail-header">
          <Link to={`/agents/${encodeURIComponent(agentId)}`} className="agent-detail-back">
            ← Agent
          </Link>
        </header>
        <h1>Edit agent</h1>
        <p>
          <strong>{agent.name}</strong> is a config-repo agent — edit it in its config-repo source,
          not the console.
        </p>
      </section>
    );
  }

  const snapshot = agent.current_version.config_snapshot;
  return (
    <AgentForm
      key={agentId}
      mode="edit"
      title="Edit agent"
      agentId={agentId}
      initial={{
        name: agent.name,
        model: readModelString(snapshot),
        system: agent.current_version.prompt_snapshot,
        skills: readAgentSkills(snapshot),
        servers: readAgentMcpServers(snapshot),
        networkProfile: readNetworkProfileName(snapshot),
        prior: readPriorToolConfig(snapshot),
        // The saved `mcp_toolset` entries VERBATIM — preserved on save when the
        // form can't currently represent a connector (fn-73 data-integrity fix).
        savedToolsets: readSavedMcpToolsets(snapshot),
      }}
    />
  );
}

// ── Shared form ──────────────────────────────────────────────────────────────

interface AgentFormProps {
  readonly mode: 'create' | 'edit';
  readonly title: string;
  readonly agentId?: string;
  readonly initial?: {
    readonly name: string;
    readonly model: string;
    readonly system: string;
    readonly skills: readonly string[];
    readonly servers: readonly string[];
    /** The agent's current network-profile name (`''` = agent default). */
    readonly networkProfile: string;
    /** Saved tool baselines overlaid onto the draft projection (edit only). */
    readonly prior: PriorToolConfig;
    /**
     * The agent's saved `mcp_toolset` entries (VERBATIM), keyed by
     * `mcp_server_name`. Edit-only — used to PRESERVE a saved connector's grant on
     * save when the form can't currently represent it (fn-73 data-integrity fix).
     */
    readonly savedToolsets: ReadonlyMap<string, Record<string, unknown>>;
  };
}

function AgentForm(props: AgentFormProps): React.ReactElement {
  const navigate = useNavigate();
  const isEdit = props.mode === 'edit';

  const [name, setName] = useState(props.initial?.name ?? '');
  const [model, setModel] = useState(props.initial?.model ?? '');
  const [system, setSystem] = useState(props.initial?.system ?? '');
  const [description, setDescription] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<readonly string[]>(
    props.initial?.skills ?? [],
  );
  // fn-73 (R5) — `gatewayEnabled` / `nonGatewaySelected` are NOT initialized
  // eagerly (the old `servers.length > 0` heuristic wrongly enabled the gateway
  // for an InfoTrack-only agent). They start OFF and are seeded ONCE AFTER
  // `mcpServers.list` resolves (the `connectorDefaults` effect below), driven by
  // the resolved connector list + the agent's saved set — no silent widening.
  // The gateway is ONE connector (fn-72): when enabled the panel requests the
  // WHOLE-GATEWAY draft so tools from every federated target appear, grouped by
  // area; non-gateway connectors are plain enable/disable toggles in form state.
  const [gatewayEnabled, setGatewayEnabled] = useState<boolean>(false);
  const [nonGatewaySelected, setNonGatewaySelected] = useState<readonly string[]>([]);
  // Optional network profile (R-quickfix-2). `''` = "(agent default)" → omit
  // `network_profile` from the submit body so the CP applies the org default.
  const [networkProfile, setNetworkProfile] = useState<string>(props.initial?.networkProfile ?? '');
  // Latest panel output (full desired baseline). `null` until the panel emits.
  const [authoringConfig, setAuthoringConfig] = useState<AuthoringToolConfig | null>(null);

  const skillsQuery = trpc.skills.list.useQuery({ limit: LIST_LIMIT });
  const serversQuery = trpc.mcpServers.list.useQuery({ limit: LIST_LIMIT });
  const networkProfilesQuery = trpc.networkProfiles.list.useQuery({ limit: LIST_LIMIT });
  // Canonical model registry (fn-72 R1) → the Model dropdown. Read-only UX data;
  // the selected `name` is forwarded verbatim to MA on save (no translation).
  const modelsQuery = trpc.models.list.useQuery();
  const modelFieldState = computeModelFieldState(modelsQuery);
  // Default the model on CREATE once the registry loads (R2): seed the
  // `is_default` item, but only when the user hasn't already chosen one and
  // we're not editing (edit seeds the saved model from its snapshot). Uses a
  // functional update keyed on the loaded data so it runs once and never fights a
  // user who clears the field.
  useEffect(() => {
    if (isEdit || modelsQuery.data === undefined) return;
    const def = pickDefaultModel(modelsQuery.data.data);
    if (def !== null) setModel((prev) => (prev.trim().length > 0 ? prev : def));
  }, [isEdit, modelsQuery.data]);
  // The resolved connector list (fn-73): the single selectable gateway row + the
  // non-gateway connectors. `null` until `mcpServers.list` resolves successfully.
  const connectors = useMemo(
    () => (serversQuery.data === undefined ? null : splitConnectors(serversQuery.data.data)),
    [serversQuery.data],
  );

  // fn-74 — the DIRECT (`supports_direct_introspection`) connector names, and the
  // SELECTED non-gateway connectors partitioned into DIRECT (→ `direct_mcp_servers`,
  // per-tool through the panel) and PLAIN (→ the fn-73 whole-server toggle).
  const directNames = useMemo(
    () => (connectors === null ? new Set<string>() : directServerNameSet(connectors.nonGateway)),
    [connectors],
  );
  const { direct: selectedDirectServers, plain: selectedPlainServers } = useMemo(
    () => partitionSelectedNonGateway(nonGatewaySelected, directNames),
    [nonGatewaySelected, directNames],
  );

  // ONE tool-data path for both modes (fn-72/fn-74): the WHOLE-GATEWAY draft
  // projection over the live catalog (a not-yet-created agent has no pinned
  // snapshot; edit re-projects so newly-surfaced targets' tools appear too) PLUS
  // any SELECTED direct connector via `direct_mcp_servers` (the CP introspects its
  // own `tools/list` per-tool). When neither the gateway nor a direct connector is
  // selected we request an empty server set → the panel shows native built-ins
  // only (a valid no-MCP agent), never a stale connector.
  const draftQuery = useMemo(
    () => buildDraftConfigurableToolsQuery({ gatewayEnabled, selectedDirectServers }),
    [gatewayEnabled, selectedDirectServers],
  );
  const panelQuery = trpc.agents.draftConfigurableTools.useQuery(draftQuery, {
    staleTime: 60_000,
  });
  const createMutation = trpc.agents.create.useMutation();
  const updateMutation = trpc.agents.update.useMutation();
  const mutation = isEdit ? updateMutation : createMutation;

  // Seed the connector toggles ONCE, AFTER the list resolves (R5 — no silent
  // widening, no eager `true`). Re-seeded on an agent-key change via the
  // render-time reset below (which re-arms `seededRef`). The `savedServers`
  // argument is `undefined` on CREATE (→ gateway on iff exactly one selectable
  // gateway; non-gateway off) and the agent's saved `mcp_server_name` set on
  // EDIT (→ gateway/non-gateway on only on a saved-name match).
  const seededRef = useRef(false);
  const savedServers = props.initial?.servers;
  // Per-entity reset (memory `per-session-reducer-reset-must-be`): the Edit loader
  // already keys `<AgentForm>` by agent id, so a real agent change REMOUNTS this
  // component and resets every `useState`. This render-time guard re-arms the
  // one-shot seed SYNCHRONOUSLY should the same instance ever be reused under a
  // new agent key (belt-and-braces) — the seed then re-runs from the new
  // `savedServers`, so no stale toggle survives into a committed paint.
  const agentKey = isEdit ? (props.agentId ?? 'edit') : 'new';
  const agentKeyRef = useRef(agentKey);
  if (agentKeyRef.current !== agentKey) {
    agentKeyRef.current = agentKey;
    seededRef.current = false;
  }
  useEffect(() => {
    if (connectors === null || seededRef.current) return;
    seededRef.current = true;
    setGatewayEnabled(initialGatewayEnabled(connectors.gateway, savedServers));
    setNonGatewaySelected(initialNonGatewaySelection(connectors.nonGateway, savedServers));
  }, [connectors, savedServers]);

  // Reconcile retained selections after a `mcpServers.list` REFETCH (fn-73 finding
  // 3): a previously-selected connector may have disappeared / flipped
  // `selectable:false`; the retained toggle state would otherwise still submit it.
  // Runs AFTER the one-shot seed so it never fights initial seeding. Clears the
  // gateway toggle when its row is gone/non-selectable and drops stale non-gateway
  // names — the UI checkbox and the submitted set then agree. A saved connector
  // dropped here is preserved verbatim by `buildAgentToolsForEdit` (not stripped).
  useEffect(() => {
    if (connectors === null || !seededRef.current) return;
    if (connectors.gateway === null) {
      setGatewayEnabled((prev) => (prev ? false : prev));
    }
    setNonGatewaySelected((prev) => reconcileNonGatewaySelection(prev, connectors.nonGateway));
  }, [connectors]);

  const loadState = toLoadState(panelQuery);
  const panelReady = loadState.kind === 'ready';
  // Edit seeds the panel from the agent's saved baselines (overlaid onto the
  // fresh projection); create uses the panel's own `defaultPanelState`. Stable
  // identity (memo on `prior`) so the panel's seed/merge effects don't churn.
  const prior = props.initial?.prior;
  const initialStateBuilder = useMemo(
    () =>
      prior === undefined
        ? undefined
        : (data: ConfigurableToolsResponse): ToolConfigPanelState =>
            // fn-74 — `directNames` makes the overlay direct-aware so a saved
            // direct WHOLE-SERVER grant seeds every introspected tool to
            // needs-approval (not disabled), preserving the grant on edit.
            applyPriorToolConfig(data, prior, directNames),
    [prior, directNames],
  );

  const nameValid = isEdit || isValidAgentName(name.trim());
  // Catalog-degraded blocks save ONLY when the gateway connector is enabled
  // (fn-73 R3) — see `isCatalogDegradedBlocking`. An agent with only non-gateway
  // connectors (gateway off) must save even if the gateway catalog is down. (The
  // panel still renders native-builtins-only when the gateway is off, so
  // `authoringConfig` is non-null regardless.)
  const catalogDegraded = isCatalogDegradedBlocking({ gatewayEnabled, authoringConfig });
  // The connector (`mcpServers.list`) query gates save: the connector checkbox is
  // hidden/unconfirmed while it's pending or errored, so saving before it
  // resolves could persist a whole-gateway grant the author never saw (fn-72 R3).
  const connectorsErrored = serversQuery.error !== null;
  const connectorsReady = !serversQuery.isPending && serversQuery.error === null;
  const canSubmit =
    nameValid &&
    model.trim().length > 0 &&
    system.trim().length > 0 &&
    connectorsReady &&
    authoringConfig !== null &&
    panelReady &&
    !catalogDegraded &&
    !mutation.isPending;

  // The FIRST unmet gating condition, as a friendly hint (R-quickfix-1): a
  // disabled submit button otherwise gives no reason. Delegates to the pure
  // `computeSubmitHint` so the order-of-checks contract is unit-testable.
  const submitBlockedReason = computeSubmitHint({
    nameValid,
    model,
    system,
    connectorsReady,
    connectorsErrored,
    catalogDegraded,
    panelReady,
    authoringReady: authoringConfig !== null,
  });

  // The saved `mcp_toolset` entries (edit only) + the set of server names the form
  // can currently represent — the inputs to `buildAgentToolsForEdit`'s
  // preserve-don't-strip partition (fn-73 data-integrity fix). The representable
  // set = the present+selectable gateway connector's name + its folded federated
  // targets (from the live catalog) + every present+selectable non-gateway row.
  const savedToolsets = props.initial?.savedToolsets;
  const representable = useMemo(() => {
    if (connectors === null) return new Set<string>();
    return representableServerNames({
      connectors,
      gatewayTargetIds: gatewayFoldedTargetIds(panelQuery.data, connectors.gateway?.name ?? null),
      // fn-74 — the direct-connector representability check needs the live panel
      // catalog + the agent's saved per-tool config (so a globally-saved InfoTrack
      // config the firm's catalog lacks is preserved verbatim, not stripped).
      panelData: panelQuery.data,
      savedToolsets,
    });
  }, [connectors, panelQuery.data, savedToolsets]);
  // Saved connectors the form can't currently represent — preserved verbatim on
  // save and surfaced read-only so the author knows they're retained (fn-73).
  const preservedNames = useMemo(
    () => preservedSavedConnectorNames({ savedToolsets, representable }),
    [savedToolsets, representable],
  );

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSubmit || authoringConfig === null) return;
    // Merge the panel output (gateway Shape-B + any SELECTED DIRECT connector's
    // per-tool/whole-server grant + native built-ins) with the PLAIN non-gateway
    // selections (fn-73/fn-74). A selected DIRECT connector (`direct_mcp_servers`)
    // is authored THROUGH the panel — `authoringConfig.servers[<direct>]` carries
    // its per-tool config (or its login-required/degraded fallback) — so it must
    // NOT also be a `nonGatewaySelections` whole-server grant; only the PLAIN
    // (non-direct) selected connectors go there. `directServerIds` tells the
    // converter which connectors are direct (so a degraded one's fallback is the
    // `always_ask` whole-server grant, not a bare default-on one).
    //
    // EDIT preserves (never strips) a SAVED connector the form can't currently
    // represent — a connector missing from `mcpServers.list` / `selectable:false`,
    // or a DIRECT connector whose saved per-tool config the firm's catalog lacks
    // (fn-74) — would otherwise seed "off" and an unrelated edit-save would
    // silently drop its persisted `mcp_toolset`. Create has no saved scope.
    const tools =
      isEdit && savedToolsets !== undefined
        ? buildAgentToolsForEdit({
            gatewayConfig: authoringConfig,
            nonGatewaySelections: selectedPlainServers,
            savedToolsets,
            representableServerNames: representable,
            directServerIds: directNames,
          })
        : buildAgentTools({
            gatewayConfig: authoringConfig,
            nonGatewaySelections: selectedPlainServers,
            directServerIds: directNames,
          });
    const shared = buildAgentSubmitShared({
      model,
      system,
      description,
      skills: selectedSkills,
      tools,
      networkProfile,
    });
    void (async () => {
      try {
        if (isEdit) {
          await updateMutation.mutateAsync({ agentId: props.agentId ?? '', ...shared });
          navigate(`/agents/${encodeURIComponent(props.agentId ?? '')}`);
        } else {
          const created = await createMutation.mutateAsync({ name: name.trim(), ...shared });
          navigate(`/agents/${encodeURIComponent(created.id)}`);
        }
      } catch {
        // The mutation surfaces its error below.
      }
    })();
  };

  const cancelHref = isEdit ? `/agents/${encodeURIComponent(props.agentId ?? '')}` : '/agents';

  return (
    <section className="page-agent-form">
      <header className="agent-detail-header">
        <Link to={cancelHref} className="agent-detail-back">
          ← {isEdit ? 'Agent' : 'Agents'}
        </Link>
      </header>
      <h1>{props.title}</h1>

      <form className="agent-form" onSubmit={submit}>
        <label className="agent-form-field">
          <span className="agent-form-label">Name</span>
          <input
            className="agent-form-input"
            type="text"
            value={name}
            placeholder="lowercase-with-hyphens"
            disabled={isEdit}
            aria-invalid={!nameValid}
            onChange={(e) => setName(e.target.value)}
          />
          {isEdit ? (
            <span className="agent-form-hint">The name is fixed after creation.</span>
          ) : (
            !nameValid &&
            name.trim().length > 0 && (
              <span className="agent-form-hint agent-form-hint--error">
                Use 2–64 lowercase letters, digits, or hyphens (start/end alphanumeric).
              </span>
            )
          )}
        </label>

        {/* Model — a dropdown sourced from the canonical model registry (R2).
            When the registry is loading/unavailable/empty it falls back to the
            EXISTING free-text id input (co-equal manual path, memory
            `ai-features-offer-manual-path`) so authoring is never blocked.
            Branches loading/error/empty distinctly (memory
            `secondary-query-joins-must-branch-on`). Not a <label> wrapper: the
            ready branch renders a <select> that carries its own aria-label. */}
        <div className="agent-form-field">
          <span className="agent-form-label">Model</span>
          {modelFieldState.kind === 'ready' ? (
            <select
              className="agent-form-input"
              aria-label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {/* Until the create-default seeds (or on an empty edit) keep a
                  placeholder so the controlled <select> has a matching option. */}
              {model.length === 0 && (
                <option value="" disabled>
                  Choose a model…
                </option>
              )}
              {modelFieldState.models.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.label}
                  {m.is_default ? ' (default)' : ''}
                </option>
              ))}
              {/* Keep the agent's saved model selectable even if the registry no
                  longer surfaces it (e.g. an older/pinned id). */}
              {savedModelNeedsOption(modelFieldState.models, model) && (
                <option value={model}>{model}</option>
              )}
            </select>
          ) : (
            <>
              <input
                className="agent-form-input"
                type="text"
                value={model}
                aria-label="Model"
                placeholder="model id (e.g. a registry model name)"
                onChange={(e) => setModel(e.target.value)}
              />
              {modelFieldState.kind === 'loading' ? (
                <span className="agent-form-hint">Loading models…</span>
              ) : modelFieldState.kind === 'error' ? (
                <span className="agent-form-hint agent-form-hint--error" role="alert">
                  Failed to load models: {modelFieldState.message}{' '}
                  <button
                    type="button"
                    className="agent-form-link"
                    onClick={() => modelsQuery.refetch()}
                  >
                    Retry
                  </button>{' '}
                  — or type a model id.
                </span>
              ) : (
                <span className="agent-form-hint">No models available — type a model id.</span>
              )}
            </>
          )}
        </div>

        <label className="agent-form-field">
          <span className="agent-form-label">System prompt</span>
          <textarea
            className="agent-form-textarea"
            value={system}
            rows={10}
            placeholder="Describe the agent's role, tone, and constraints."
            onChange={(e) => setSystem(e.target.value)}
          />
        </label>

        <label className="agent-form-field">
          <span className="agent-form-label">Description (optional)</span>
          <input
            className="agent-form-input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        {/* Skills — multi-select referencing existing config-repo skills. */}
        <fieldset className="agent-form-fieldset">
          <legend className="agent-form-label">Skills</legend>
          {skillsQuery.isPending ? (
            <p className="agent-form-hint">Loading skills…</p>
          ) : skillsQuery.error !== null ? (
            <p className="agent-form-hint agent-form-hint--error" role="alert">
              Failed to load skills: {skillsQuery.error.message}{' '}
              <button
                type="button"
                className="agent-form-link"
                onClick={() => skillsQuery.refetch()}
              >
                Retry
              </button>
            </p>
          ) : skillsQuery.data.data.length === 0 ? (
            <p className="agent-form-hint">No skills available.</p>
          ) : (
            <ul className="agent-form-checklist">
              {skillsQuery.data.data.map((skill) => (
                <li key={skill.skill_name}>
                  <label className="agent-form-check">
                    <input
                      type="checkbox"
                      checked={selectedSkills.includes(skill.skill_name)}
                      onChange={(e) =>
                        setSelectedSkills((prev) =>
                          e.target.checked
                            ? [...prev, skill.skill_name]
                            : prev.filter((s) => s !== skill.skill_name),
                        )
                      }
                    />
                    <span>{skill.skill_name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        {/* Connectors — the gateway as ONE grouped connector (fn-72, its
            per-tool view in the Tool permissions panel below) PLUS every OTHER
            registered MCP connector (fn-73 R1) as a plain enable/disable toggle.
            The gateway row drives the WHOLE-GATEWAY draft so tools from every
            federated target (Smokeball / Forms / Matter types / Archie) appear in
            the panel, grouped by area. A non-gateway connector (e.g. InfoTrack) is
            a whole-server grant LOCKED to `always_ask` — the platform can't
            enumerate a direct-url server's tools, so it offers enable/disable only
            (no `always_allow`; denylist-safe). Non-`ok` rows render DISABLED with
            their scan status (R-minor). Branches loading/error/empty distinctly
            (memory `secondary-query-joins-must-branch-on`). The console does NOT
            hardcode the gateway name — every row's label is its registry name. */}
        <fieldset className="agent-form-fieldset">
          <legend className="agent-form-label">Connectors</legend>
          {serversQuery.isPending ? (
            <p className="agent-form-hint" aria-busy="true">
              Loading connectors…
            </p>
          ) : serversQuery.error !== null ? (
            <p className="agent-form-hint agent-form-hint--error" role="alert">
              Failed to load connectors: {serversQuery.error.message}{' '}
              <button
                type="button"
                className="agent-form-link"
                onClick={() => serversQuery.refetch()}
              >
                Retry
              </button>
            </p>
          ) : connectors === null || serversQuery.data.data.length === 0 ? (
            <p className="agent-form-hint">No connectors available.</p>
          ) : (
            <ul className="agent-form-checklist">
              {/* The single selectable gateway connector (fn-72 grouped view). */}
              {connectors.gateway !== null && (
                <li key={connectors.gateway.name}>
                  <label className="agent-form-check">
                    <input
                      type="checkbox"
                      checked={gatewayEnabled}
                      onChange={(e) => setGatewayEnabled(e.target.checked)}
                    />
                    <span>{connectors.gateway.name}</span>
                  </label>
                  <span className="agent-form-hint">Gateway — per-tool permissions below.</span>
                </li>
              )}
              {/* A gateway-matched but non-selectable row (non-`ok` scan or a
                  >1-match conflict): shown DISABLED with its status (R-minor). */}
              {connectors.gatewayDisabled !== null && (
                <li key={connectors.gatewayDisabled.name}>
                  <label className="agent-form-check">
                    <input type="checkbox" checked={false} disabled />
                    <span>{connectors.gatewayDisabled.name}</span>
                  </label>
                  <span className="agent-form-hint agent-form-hint--warn">
                    {connectorStatusText(connectors.gatewayDisabled.scan_status)}
                  </span>
                </li>
              )}
              {/* Every non-gateway connector. Two treatments (fn-74):
                  - DIRECT (`supports_direct_introspection`, e.g. InfoTrack):
                    enabling it sends the connector in `direct_mcp_servers` so the
                    CP introspects its OWN `tools/list` — its per-tool config (or a
                    "log in to InfoTrack first" / degraded whole-server fallback)
                    renders in the Tool permissions panel below, exactly like the
                    gateway. Direct tools are always Always-ask (HITL on every call;
                    never Always-allow) — the gateway interceptor can't gate a
                    direct-url server.
                  - PLAIN non-gateway: NO per-tool view (no reader for its
                    `tools/list`), so enabling it is ONE whole-server grant locked
                    to `always_ask` (denylist-safe; the fn-73 treatment).
                  Non-selectable rows are disabled with their scan status. */}
              {connectors.nonGateway.map((row) => {
                const isDirect = directNames.has(row.name);
                return (
                  <li key={row.name}>
                    <label className="agent-form-check">
                      <input
                        type="checkbox"
                        checked={row.selectable && nonGatewaySelected.includes(row.name)}
                        disabled={!row.selectable}
                        onChange={(e) =>
                          setNonGatewaySelected((prev) =>
                            e.target.checked
                              ? prev.includes(row.name)
                                ? prev
                                : [...prev, row.name]
                              : prev.filter((n) => n !== row.name),
                          )
                        }
                      />
                      <span>{row.name}</span>
                    </label>
                    {row.selectable ? (
                      <span className="agent-form-hint">
                        {isDirect
                          ? 'Direct connector — per-tool permissions below (every call asks).'
                          : 'Always ask (HITL) on every call.'}
                      </span>
                    ) : (
                      <span className="agent-form-hint agent-form-hint--warn">
                        {connectorStatusText(row.scan_status)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {/* Saved connectors the form can't currently represent (missing from the
              registry or non-selectable). Their persisted grants are PRESERVED
              VERBATIM on save (never stripped) — surfaced read-only so the author
              knows they're retained but not editable here (fn-73). */}
          {isEdit && preservedNames.length > 0 && (
            <p className="agent-form-hint agent-form-hint--warn" data-testid="preserved-connectors">
              Saved (not editable right now — preserved on save): {preservedNames.join(', ')}.
            </p>
          )}
        </fieldset>

        {/* Network profile — OPTIONAL single-select referencing config-repo
            network profiles (fn-68). Blank = "(agent default)" → omit
            `network_profile` so the CP applies the org default. Branches
            loading/error/empty distinctly (memory
            `secondary-query-joins-must-branch-on`). */}
        {/* Not a <label> wrapper: the control is conditionally rendered
            (loading/error branches show no <select>), so a wrapping <label>
            has no associable control. The <select> carries its own aria-label. */}
        <div className="agent-form-field">
          <span className="agent-form-label">Network profile (optional)</span>
          {networkProfilesQuery.isPending ? (
            <span className="agent-form-hint">Loading network profiles…</span>
          ) : networkProfilesQuery.error !== null ? (
            <span className="agent-form-hint agent-form-hint--error" role="alert">
              Failed to load network profiles: {networkProfilesQuery.error.message}{' '}
              <button
                type="button"
                className="agent-form-link"
                onClick={() => networkProfilesQuery.refetch()}
              >
                Retry
              </button>
            </span>
          ) : (
            <select
              className="agent-form-input"
              aria-label="Network profile"
              value={networkProfile}
              onChange={(e) => setNetworkProfile(e.target.value)}
            >
              <option value="">(agent default)</option>
              {networkProfilesQuery.data.data.map((profile) => (
                <option key={profile.name} value={profile.name}>
                  {profile.name}
                  {profile.is_default ? ' (default)' : ''}
                </option>
              ))}
              {/* Keep the agent's saved profile selectable even if it isn't in
                  the current list (e.g. unscanned / filtered out). */}
              {networkProfile.length > 0 &&
                !networkProfilesQuery.data.data.some((p) => p.name === networkProfile) && (
                  <option value={networkProfile}>{networkProfile}</option>
                )}
            </select>
          )}
          {!networkProfilesQuery.isPending &&
            networkProfilesQuery.error === null &&
            networkProfilesQuery.data.data.length === 0 &&
            (networkProfile.length === 0 ? (
              <span className="agent-form-hint">
                No network profiles available — the agent default applies.
              </span>
            ) : (
              <span className="agent-form-hint">
                No network profiles available — the saved profile “{networkProfile}” stays in effect
                unless you change it.
              </span>
            ))}
        </div>

        {/* Per-tool baseline permissions — the productized fn-69 panel in
            authoring mode. Branches loading/error/empty/degraded itself.

            R7 DEFERRAL (fn-72): tools are grouped by AREA/target here, NOT by
            read/write annotation, and there is no "mutating → Always ask" smart
            default. That grouping is intentionally deferred to the MCP-server-
            target rewrite: AgentCore's Lambda-target tool schema carries no
            `annotations` field, so SBMCP's `readOnlyHint` can't reach the
            platform through the gateway today. UNLOCK CONDITION: route SBMCP as
            an MCP-server target in DYNAMIC ListingMode so the gateway proxies
            the live `tools/list` (which carries `readOnlyHint`); only then can
            the panel group by read/write and apply mutating-default smartness.
            See docs/engineering-changelog.md (fn-72) + the fn-72 spec R7. */}
        <fieldset className="agent-form-fieldset">
          <legend className="agent-form-label">Tool permissions</legend>
          <AgentToolConfigPanel
            loadState={loadState}
            agentKey={agentKey}
            mode="authoring"
            groupBy="area"
            onRetry={() => panelQuery.refetch()}
            onAuthoringChange={setAuthoringConfig}
            {...(initialStateBuilder !== undefined ? { initialStateBuilder } : {})}
          />
          {catalogDegraded && (
            <output className="agent-form-hint agent-form-hint--warn">
              The tool catalog couldn't be fully read, so saving is disabled until it loads — use
              Retry above.
            </output>
          )}
        </fieldset>

        {mutation.error !== null && (
          <p className="agent-form-error" role="alert">
            Failed to save agent: {mutation.error.message}
          </p>
        )}

        <div className="agent-form-actions">
          <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create agent'}
          </button>
          <Link to={cancelHref} className="agent-form-link">
            Cancel
          </Link>
          {/* Why the submit is disabled — only while it IS disabled and not
              mid-submit (the button shows "Saving…" then). */}
          {!canSubmit && !mutation.isPending && submitBlockedReason !== null && (
            <span className="agent-form-hint" data-testid="agent-form-submit-hint">
              {submitBlockedReason}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
